import { useEffect, useRef } from "react";
import type L from "leaflet";
import type { SocietyGroup } from "@/data/universityLocations";
import "leaflet/dist/leaflet.css";

type MarkerData = {
  society: SocietyGroup["items"][number];
  position: [number, number];
};

const SELECTED_DOT_CLASS = "society-dot-selected";

const getDotElement = (marker: L.Marker | undefined) =>
  marker?.getElement()?.querySelector<HTMLElement>(".society-dot") ?? null;

export default function UniversityMap({
  groups,
  selectedSocietyId,
}: {
  groups: SocietyGroup[];
  selectedSocietyId?: string | null;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapRefObj = useRef<L.Map | null>(null);
  const leafletRef = useRef<typeof L | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const selectedIdRef = useRef<string | null>(selectedSocietyId ?? null);
  const activeMarkerIdRef = useRef<string | null>(null);

  const focusSociety = (id: string | null) => {
    const map = mapRefObj.current;
    if (!map) return;

    const activeId = activeMarkerIdRef.current;
    activeMarkerIdRef.current = null;
    if (activeId) {
      const activeMarker = markersRef.current.get(activeId);
      activeMarker?.closeTooltip();
      getDotElement(activeMarker)?.classList.remove(SELECTED_DOT_CLASS);
    }

    if (!id) return;

    const marker = markersRef.current.get(id);
    if (!marker) return;

    map.setView(marker.getLatLng(), 14, { animate: true });
    marker.openTooltip();
    getDotElement(marker)?.classList.add(SELECTED_DOT_CLASS);
    activeMarkerIdRef.current = id;
  };

  useEffect(() => {
    if (!mapRef.current || mapRefObj.current) return;
    let cancelled = false;
    const markers: MarkerData[] = [];

    for (const group of groups) {
      const location = group.location;
      if (location === null) continue;

      group.items.forEach((society, index) => {
        const angle = index * 2.399963;
        const ring = Math.floor(index / 12);
        const radius = 0.0004 * (ring + 1);
        const dy = Math.sin(angle) * radius;
        const dx = Math.cos(angle) * radius;

        markers.push({
          society,
          position: [location.lat + dy, location.lng + dx],
        });
      });
    }

    const init = async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !mapRef.current) return;

      const map = L.map(mapRef.current, {
        center: [22.35, 114.15],
        zoom: 11,
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: false,
        keyboard: false,
      });

      L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 16 },
      ).addTo(map);

      mapRefObj.current = map;
      leafletRef.current = L;

      for (const markerData of markers) {
        const icon = L.divIcon({
          className: "",
          html: `<div class="society-marker"><div class="society-dot"></div></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });

        const marker = L.marker(markerData.position, { icon }).addTo(map);
        marker.bindTooltip(markerData.society.name, {
          permanent: false,
          direction: "top",
          offset: [0, -8],
          className: "society-tooltip",
        });
        markersRef.current.set(markerData.society.id, marker);
      }

      focusSociety(selectedIdRef.current);
    };

    init();

    return () => {
      cancelled = true;
      if (mapRefObj.current) {
        mapRefObj.current.remove();
      }
      mapRefObj.current = null;
      leafletRef.current = null;
      markersRef.current.clear();
      activeMarkerIdRef.current = null;
    };
  }, [groups]);

  useEffect(() => {
    selectedIdRef.current = selectedSocietyId ?? null;
    focusSociety(selectedSocietyId ?? null);
  }, [selectedSocietyId, groups]);

  return (
    <>
      <style>{`
        @keyframes markerPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.3); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes markerFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .society-marker {
          display: flex;
          align-items: center;
          justify-content: center;
          animation: markerPop 0.2s ease-out forwards, markerFloat 2.5s ease-in-out infinite;
          animation-delay: var(--pop-delay, 0s), calc(var(--pop-delay, 0s) + 0.2s);
        }
        .society-dot {
          width: 12px;
          height: 12px;
          background: #22c55e;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 0 6px rgba(34,197,94,0.5);
        }
        .society-tooltip {
          background: #1f2937;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
          font-family: 'Quicksand', 'PingFang TC', Arial, sans-serif;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .society-tooltip::before {
          border-top-color: #1f2937 !important;
        }
        .society-dot-selected {
          box-shadow: 0 0 0 3px rgba(255,255,255,0.9), 0 0 12px rgba(34,197,94,0.9);
          filter: brightness(1.15);
        }
      `}</style>
      <div
        ref={mapRef}
        className="w-full rounded-2xl overflow-hidden shadow-lg border border-[#e5d6c3]/50 relative z-0"
        style={{ height: "clamp(300px, 50vw, 450px)" }}
      />
    </>
  );
}
